
import React, { useState } from 'react';
import { Company } from '@/lib/types';
import { formatDateYMD } from '@/utils/dateFormat';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Edit, Trash2, Building, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { deleteCompany } from '@/services/companyService';
import { confirm } from '@/components/ui/confirm';

interface CompanyManagementProps {
  companies: Company[];
  isLoading: boolean;
  onRefresh: () => void;
}

export const CompanyManagement: React.FC<CompanyManagementProps> = ({ 
  companies, 
  isLoading,
  onRefresh
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCompanies = companies.filter(company => 
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (company.industry && company.industry.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleCreateCompany = () => {
    navigate('/companies/new');
  };

  const handleEditCompany = (id: string) => {
    navigate(`/companies/${id}/edit`);
  };

  const handleDeleteCompany = async (id: string, name: string) => {
    confirm({
      title: "Delete Company",
      description: `Are you sure you want to delete ${name}? This action cannot be undone and will remove all company data.`,
      onConfirm: async () => {
        try {
          await deleteCompany(id);
          toast({
            title: "Company deleted",
            description: `${name} has been successfully deleted.`,
          });
          onRefresh();
        } catch (error: any) {
          toast({
            title: "Error",
            description: error.message || "Failed to delete company",
            variant: "destructive",
          });
        }
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Company Management</CardTitle>
            <CardDescription>
              View and manage companies in the system.
            </CardDescription>
          </div>
          <Button onClick={handleCreateCompany}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
      </CardHeader>
      
      <div className="px-6 pb-2">
        <div className="relative w-full">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search companies..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex justify-center p-10">Loading companies...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    No companies found matching your search criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        {company.name}
                      </div>
                    </TableCell>
                    <TableCell>{company.industry || '—'}</TableCell>
                    <TableCell>
                      {company.size ? <Badge variant="outline">{company.size}</Badge> : '—'}
                    </TableCell>
                    <TableCell>
                      {company.website ? (
                        <a 
                          href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <Globe className="h-3 w-3" />
                          {company.website}
                        </a>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {formatDateYMD(company.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCompany(company.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCompany(company.id, company.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {!isLoading && filteredCompanies.length > 0 && (
        <CardFooter className="py-4 border-t">
          <div className="text-sm text-muted-foreground">
            Total: {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};
